/**
 * Shares the active Metrics date range with CSV download prompts.
 *
 * 1. Each overview provides the range used by its queries or passes it directly when its provider
 *    is below the download control.
 * 2. Download controls format both dates through the Metrics translation key.
 * Continues in `SectionWrap.tsx` and Metrics download controls.
 */

import React, { createContext, type FC, useContext } from 'react';

import { useTranslation } from '@modernloop/i18n';

/** Holds the date bounds used by one Metrics overview query. */
type MetricsDownloadDateRange = { startAt: Date; endAt: Date };

/** Makes the active Metrics date range available to download controls. */
const MetricsDownloadDateRangeContext = createContext<MetricsDownloadDateRange | null>(null);

/** Supplies the query date range used to describe a Metrics CSV download. */
export const MetricsDownloadDateRangeProvider: FC<{
  children: React.ReactNode;
  dateRange: MetricsDownloadDateRange;
}> = ({ children, dateRange }) => (
  <MetricsDownloadDateRangeContext.Provider value={dateRange}>{children}</MetricsDownloadDateRangeContext.Provider>
);

/** Formats a localized description for the active section and its date range. */
export const useMetricsDownloadDescription = (
  section: string,
  explicitDateRange?: MetricsDownloadDateRange
): string => {
  const { t } = useTranslation();
  const contextDateRange = useContext(MetricsDownloadDateRangeContext);
  const dateRange = explicitDateRange ?? contextDateRange;
  if (!dateRange) {
    return t('hiring-dashboard-metrics:metrics.downloadReason.sectionOnlyDataDescription', { section });
  }

  return t('hiring-dashboard-metrics:metrics.downloadReason.dataDescription', {
    section,
    startAt: dateRange.startAt,
    endAt: dateRange.endAt,
  });
};
